import { oc } from "@orpc/contract";
import { z } from "zod";

export const AIAgentSchema = z.enum([
  "web_navigation",
  "business_intelligence",
  "assistant",
  "unknown",
  "spreadsheet",
  "chat",
]);

export const AIThreadStatusSchema = z.enum(["active", "closed"]);

export const ToolCallSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  args: z.record(z.string(), z.unknown()),
  result: z.unknown().optional(),
});

export const ToolCallAuxiliaryContentSchema = z
  .object({
    toolCalls: z.array(ToolCallSchema).optional(),
  })
  .nullable();

export const AIThreadMessageDTOSchema = z.object({
  id: z.string().uuid(),
  threadId: z.string().uuid(),
  senderType: z.enum(["user", "assistant", "system"]),
  senderId: z.string().uuid().nullable(),
  messageContent: z.string(),
  messageAuxiliaryContent: ToolCallAuxiliaryContentSchema,
  llmResponseMetadata: z.unknown().nullable(),
  llmProvider: z.enum(["openai", "anthropic"]),
  llmConfig: z.unknown().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const AIThreadDTOSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  agent: AIAgentSchema,
  topic: z.string().nullable(),
  status: AIThreadStatusSchema,
  messages: z.array(AIThreadMessageDTOSchema),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const AIThreadSummaryDTOSchema = AIThreadDTOSchema.omit({
  messages: true,
});

export const PaginationSchema = z.object({
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});

export const GetThreadInputSchema = z.object({
  threadId: z.string().uuid(),
});

export const ListThreadsInputSchema = z.object({
  limit: z.number().min(1).max(100).default(25).optional(),
  offset: z.number().min(0).default(0).optional(),
});

export const ListThreadsOutputSchema = z.object({
  items: z.array(AIThreadSummaryDTOSchema),
  pagination: PaginationSchema,
});

export const threadsContracts = {
  get: oc.input(GetThreadInputSchema).output(AIThreadDTOSchema).route({
    method: "POST",
    path: "/ai/threads/get",
  }),
  list: oc.input(ListThreadsInputSchema).output(ListThreadsOutputSchema).route({
    method: "POST",
    path: "/ai/threads/list",
  }),
};

export type AIAgent = z.infer<typeof AIAgentSchema>;
export type AIThreadStatus = z.infer<typeof AIThreadStatusSchema>;
export type ToolCall = z.infer<typeof ToolCallSchema>;
export type ToolCallAuxiliaryContent = z.infer<
  typeof ToolCallAuxiliaryContentSchema
>;
export type AIThreadMessageDTO = z.infer<typeof AIThreadMessageDTOSchema>;
export type AIThreadDTO = z.infer<typeof AIThreadDTOSchema>;
export type AIThreadSummaryDTO = z.infer<typeof AIThreadSummaryDTOSchema>;
export type Pagination = z.infer<typeof PaginationSchema>;
export type GetThreadInput = z.infer<typeof GetThreadInputSchema>;
export type ListThreadsInput = z.infer<typeof ListThreadsInputSchema>;
export type ListThreadsOutput = z.infer<typeof ListThreadsOutputSchema>;
